import { AlignCenter, AlignEnd, AlignStart } from '@styled-icons/bootstrap';
import { HandPointer } from '@styled-icons/fa-solid';
import { AlignType, DreamBlock } from '@typedream/core';
import { setNodes } from '@udecode/plate-core';
import classNames from 'classnames';
import { memo } from 'react';
import shallow from 'zustand/shallow';
import Tooltip from '../../../components/Tooltip';
import {
  DEFAULT_ALIGN_TYPE,
  ELEMENT_DEFAULT_ALIGN_TYPE,
} from '../../../constants';
import { useTPlateEditorRef } from '../../../plate';
import { setColumnItemsWidthToFitContent } from '../../../nodes/column/transforms';
import { getSelectedElementEntry } from '../../../plugins/elementSelection';
import { useDreamEditorStore } from '../../../plugins/store';
import styles from '../SideToolbar.module.css';

function Align() {
  const editor = useTPlateEditorRef();

  const [currentAlign, shouldShowColumnDynamicOption] = useDreamEditorStore(
    () => {
      const element = getSelectedElementEntry<Extract<DreamBlock, { align?: string }>>(
        editor,
      )?.[0];

      const defaultElementType = (element && ELEMENT_DEFAULT_ALIGN_TYPE[element.type])
        || DEFAULT_ALIGN_TYPE;

      return [element?.align || defaultElementType, element?.type === 'column'];
    },
    shallow,
  );

  const setAlign = (v: AlignType) => {
    const entry = getSelectedElementEntry(editor);

    if (!entry) return;

    const [element, path] = entry;

    setNodes(editor, { align: v }, { at: path });

    // Adjust column_items width upon changing alignment of column to left/center/right
    if (
      element?.type === 'column'
      && v !== 'column_dynamic'
      && currentAlign === 'column_dynamic'
    ) {
      setColumnItemsWidthToFitContent(editor, path);
    }
  };

  return (
    <div className={styles.section}>
      <span className={styles.label}>Align</span>
      <div className={styles.menuGroup}>
        <div className={styles.menuItem}>
          <Tooltip center="Align Left">
            <button
              type="button"
              className={classNames(styles.toggleButton, {
                [styles.active]: currentAlign === 'align_left',
              })}
              onClick={() => setAlign('align_left')}
            >
              <AlignStart size={16} />
            </button>
          </Tooltip>
          <Tooltip center="Align Center">
            <button
              type="button"
              className={classNames(styles.toggleButton, {
                [styles.active]: currentAlign === 'align_center',
              })}
              onClick={() => setAlign('align_center')}
            >
              <AlignCenter size={16} />
            </button>
          </Tooltip>
          <Tooltip center="Align Right">
            <button
              type="button"
              className={classNames(styles.toggleButton, {
                [styles.active]: currentAlign === 'align_right',
              })}
              onClick={() => setAlign('align_right')}
            >
              <AlignEnd size={16} />
            </button>
          </Tooltip>
          {shouldShowColumnDynamicOption && (
            <Tooltip center="Show Resize Handle">
              <button
                type="button"
                className={classNames(styles.toggleButton, {
                  [styles.active]: currentAlign === 'column_dynamic',
                })}
                onClick={() => setAlign('column_dynamic')}
              >
                <HandPointer size={14} />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(Align);
